import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { resolveImg } from "./api";

const LightboxContext = createContext(null);
const EASE = [0.16, 1, 0.3, 1];

export const LightboxProvider = ({ children }) => {
  const [items, setItems] = useState([]);
  const [index, setIndex] = useState(0);
  const [open, setOpen] = useState(false);

  const show = useCallback((list, startIndex = 0) => {
    const clean = (list || []).filter((i) => i && i.img);
    if (!clean.length) return;
    setItems(clean);
    setIndex(Math.max(0, Math.min(startIndex, clean.length - 1)));
    setOpen(true);
  }, []);

  const close = useCallback(() => setOpen(false), []);
  const next = useCallback(() => setIndex((i) => (i + 1) % items.length), [items.length]);
  const prev = useCallback(() => setIndex((i) => (i - 1 + items.length) % items.length), [items.length]);

  useEffect(() => {
    if (!open) return;
    document.body.style.overflow = "hidden";
    const onKey = (e) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", onKey);
    return () => { document.body.style.overflow = ""; window.removeEventListener("keydown", onKey); };
  }, [open, close, next, prev]);

  const item = items[index];
  const multi = items.length > 1;

  return (
    <LightboxContext.Provider value={{ show }}>
      {children}
      {createPortal(
        <AnimatePresence>
          {open && item && (
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }}
              className="fixed inset-0 z-[100] flex flex-col items-center justify-center bg-cantina-cocoadeep/95 backdrop-blur-md px-4 py-6"
              onClick={close}
              data-testid="lightbox-overlay"
            >
              <button onClick={close} className="absolute top-5 right-5 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-cantina-cream flex items-center justify-center transition-colors" data-testid="lightbox-close" aria-label="Close">
                <X size={22} />
              </button>

              {multi && (
                <>
                  <button onClick={(e) => { e.stopPropagation(); prev(); }} className="absolute left-3 md:left-8 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 text-cantina-cream flex items-center justify-center transition-colors z-10" data-testid="lightbox-prev" aria-label="Previous">
                    <ChevronLeft size={26} />
                  </button>
                  <button onClick={(e) => { e.stopPropagation(); next(); }} className="absolute right-3 md:right-8 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 text-cantina-cream flex items-center justify-center transition-colors z-10" data-testid="lightbox-next" aria-label="Next">
                    <ChevronRight size={26} />
                  </button>
                </>
              )}

              <AnimatePresence mode="wait">
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.94, y: 12 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.98 }}
                  transition={{ duration: 0.4, ease: EASE }}
                  className="flex flex-col items-center max-w-[92vw] md:max-w-[70vw]"
                  onClick={(e) => e.stopPropagation()}
                >
                  <img src={resolveImg(item.img)} alt={item.name} className="max-h-[68vh] w-auto rounded-2xl border-4 border-white/10 shadow-2xl object-contain" data-testid="lightbox-image" />
                  <div className="mt-5 text-center max-w-xl">
                    <div className="flex items-center justify-center gap-3 flex-wrap">
                      {item.name && <h3 className="font-display text-2xl md:text-3xl font-bold text-cantina-cream">{item.name}</h3>}
                      {item.price && <span className="font-sans text-lg font-bold text-cantina-marigold">{item.price}</span>}
                    </div>
                    {item.desc && <p className="mt-2 font-sans text-sm md:text-base text-cantina-cream/70 leading-relaxed">{item.desc}</p>}
                    {multi && <p className="mt-3 font-mono text-xs tracking-widest text-cantina-cream/40">{index + 1} / {items.length}</p>}
                  </div>
                </motion.div>
              </AnimatePresence>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </LightboxContext.Provider>
  );
};

export const useLightbox = () => {
  const ctx = useContext(LightboxContext);
  return ctx || { show: () => {} };
};
